// Canonical "start a new order" action.
//
// SINGLE SOURCE OF TRUTH for starting a brand-new order. The portal must have
// exactly ONE primary action for this — it lives in the top nav (Layout) and
// is rendered by this component. Hitting NEW_ORDER_PATH mints a fresh draft
// (QuoteNew) and hands off to the configurator at /quotes/:id.
//
// SAFEGUARD — do not duplicate this action:
//   • Do NOT hand-roll another `<Link to="/quotes/new">Start a new order</Link>`
//     or a second primary "Start a new order" button anywhere. The orders list
//     (QuoteList / "My orders") is view-only by design and must not carry its
//     own create button — that was the duplication this component removed.
//   • For an inline contextual nudge inside prose (e.g. a welcome sentence or a
//     zero-orders empty state), link to NEW_ORDER_PATH so the route stays
//     defined in one place — but keep it a plain text link, never a competing
//     primary button.
//   • Keep it UNCONDITIONAL: one label, one target, in every session — including
//     one an Argo rep opened from a Nexus Opportunity. No relabel, no alternate
//     destination, no confirm dialog. This CTA sits in the global header, so it
//     renders on the order it would offer to "continue"; and a session that
//     stops offering a new order leaves a rep with no way to start one. The
//     duplicate-order protection lives on the entry screen (QuoteNew, which
//     names the deal's order in progress and still shows the door picker) and on
//     the server, which allows at most one open order per Opportunity.
// Reordering / "use as starting point" is a *different* behavior (server-side
// clone of an existing quote) and is not this — see QuoteList.handleClone.

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

/** The one route that begins a new, blank order. */
export const NEW_ORDER_PATH = '/quotes/new';

/** The portal's single primary "start a new order" action (top-nav CTA). */
export function StartNewOrderButton(): JSX.Element {
  const { t } = useTranslation('common');
  return (
    <Link to={NEW_ORDER_PATH} className="app-header__cta">
      {t('nav.startNewOrder')}
    </Link>
  );
}
